import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconRefresh } from '@tabler/icons-react';
import type { Vendor, VendorImportBatch, VendorImportRow } from '@techgitter/contracts';
import { getVendors } from '../../services/vendorService';
import { fetchInactiveRows, fetchImportBatches } from '../../services/reconciliationApi';
import { textMatches } from './reconciliationFilter';
import { ReconciliationFilterFields } from './ReconciliationFilterFields';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX } from '../../styles/designSystem';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/**
 * The Inactive area: dormant subscriptions (suspended/expired/disabled with an
 * auto-renew date more than 3 days from today) for a supplier's latest import.
 * The engine parks these — they never appear as breaks, variances, or in the
 * unmatched worklist. Read-only.
 */
interface Props {
  customerFilter?: string;
  productFilter?: string;
  onCustomerFilterChange?: (v: string) => void;
  onProductFilterChange?: (v: string) => void;
}

export const InactivePanel: React.FC<Props> = ({
  customerFilter = '',
  productFilter = '',
  onCustomerFilterChange,
  onProductFilterChange,
}) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>('');
  const [batch, setBatch] = useState<VendorImportBatch | null>(null);
  const [rows, setRows] = useState<VendorImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendorId, label: v.name })),
    [vendors],
  );

  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const suppliers = all.filter((v) => v.isSubscriptionSupplier);
        setVendors(suppliers);
        if (suppliers.length && !vendorId) setVendorId(suppliers[0].vendorId);
      })
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError('Could not load subscription suppliers.');
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!vendorId) return;
      setLoading(true);
      setError(null);
      try {
        const [batches, inactive] = await Promise.all([
          fetchImportBatches({ vendorId, top: 1 }, signal),
          fetchInactiveRows({ vendorId }, signal),
        ]);
        if (signal?.aborted) return;
        setBatch(batches[0] ?? null);
        setRows(inactive);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load inactive rows');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [vendorId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const sorted = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            textMatches(customerFilter, r.customerIdentifier) &&
            textMatches(productFilter, r.sku),
        )
        .sort((a, b) => (a.customerIdentifier ?? '').localeCompare(b.customerIdentifier ?? '')),
    [rows, customerFilter, productFilter],
  );

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Supplier"
          value={vendorId}
          onChange={setVendorId}
          options={vendorOptions}
        />
        {batch && (
          <Typography sx={{ fontSize: 12, color: '#888' }}>
            Latest import: {fmtDate(batch.importedAt)} · {batch.rowCount ?? '—'} rows
          </Typography>
        )}
        <ReconciliationFilterFields
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={onCustomerFilterChange ?? (() => {})}
          onProductFilterChange={onProductFilterChange ?? (() => {})}
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
          Dormant subscriptions (suspended / expired / disabled, not within 3 days of
          renewal). Parked — not counted as breaks or variances.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : sorted.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#888', px: 0.5, py: 2 }}>
            No inactive subscriptions in this supplier’s latest import. 🎉
          </Typography>
        ) : (
          <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
            <Box sx={{ p: '12px 16px', borderBottom: '0.5px solid #e2eaf3', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Inactive</Typography>
              <Chip>{sorted.length}</Chip>
            </Box>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>SKU</DataTableHeadCell>
                  <DataTableHeadCell>Term</DataTableHeadCell>
                  <DataTableHeadCell align="right">Seats</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Renewal</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <DataTableRow key={r.id}>
                    <DataTableCell>{r.customerIdentifier ?? '—'}</DataTableCell>
                    <DataTableCell>{r.sku ?? '—'}</DataTableCell>
                    <DataTableCell>{r.billingTerm ? <Chip>{r.billingTerm}</Chip> : '—'}</DataTableCell>
                    <DataTableCell align="right">{r.seats ?? '—'}</DataTableCell>
                    <DataTableCell>
                      {r.subscriptionStatus ? <Chip>{r.subscriptionStatus}</Chip> : '—'}
                    </DataTableCell>
                    <DataTableCell muted>{fmtDate(r.renewalDate)}</DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          </Box>
        )}
      </Box>
    </>
  );
};
